/**
 * /admin/customers — Master list of every tenant on Cognara.
 * Two tabs (Organizations vs Personal), KPI strip, sortable table,
 * search, CSV export. Click a row → drilldown.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, User, Search, Download, MapPin, Activity,
  Wallet, MessageSquare, IndianRupee, DollarSign, Users as UsersIcon,
  TrendingUp, ChevronRight, Lightbulb, Plus, X,
} from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { adminCustomersApi, adminOrgsApi } from '../../services/api';
import { toast } from 'sonner';

const fmtMoney = (v, cur = 'INR') => {
  if (v == null) return '—';
  const sym = cur === 'INR' ? '₹' : '$';
  return `${sym}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

const TILE = ({ label, value, sub, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-xl p-4"
    style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
    data-testid={`tile-${label.toLowerCase().replace(/\s+/g,'-')}`}
  >
    <div className="flex items-center justify-between">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <Icon size={14} />
      </div>
    </div>
    <div className="mt-3 text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>{label}</div>
    <div className="mt-1 font-heading text-2xl font-semibold text-nexus-text-primary">{value}</div>
    {sub && <div className="text-[11px] text-nexus-text-tertiary mt-0.5">{sub}</div>}
  </motion.div>
);

const AdminCustomers = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('revenue');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const setData = ({ items, total }) => { setItems(items); setTotal(total); };

  useEffect(() => {
    adminCustomersApi.overview().then(({ data }) => setOverview(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const handler = setTimeout(() => {
      adminCustomersApi.list({ type, q: q || undefined, sort, order, limit: 100, offset: 0 })
        .then(({ data }) => { setItems(data.items); setTotal(data.total); })
        .catch(() => toast.error('Failed to load customers'))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(handler);
  }, [type, q, sort, order]);

  const handleSort = (col) => {
    if (sort === col) setOrder(order === 'desc' ? 'asc' : 'desc');
    else { setSort(col); setOrder('desc'); }
  };

  const handleExport = async () => {
    try {
      const { data } = await adminCustomersApi.exportCsv();
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `customers-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const tc = overview?.tenant_counts || {};
  const revInr = overview?.revenue_by_currency?.find(r => r._id === 'INR');
  const revUsd = overview?.revenue_by_currency?.find(r => r._id === 'USD');
  const totalCreditsSold = (revInr?.credits || 0) + (revUsd?.credits || 0);
  const totalCreditsConsumed = (overview?.consumption_by_model || []).reduce((s, m) => s + (m.credits || 0), 0);
  const topCountries = useMemo(
    () => (overview?.geography || []).filter(g => g.country_code !== '??').slice(0, 6),
    [overview?.geography]
  );

  return (
    <div className="space-y-6" data-testid="admin-customers">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>CONTROL PLANE</div>
          <h1 className="font-heading text-3xl font-semibold text-nexus-text-primary mt-1">Customers</h1>
          <p className="text-[12px] text-nexus-text-tertiary mt-1">
            Every organization, personal account, conversation, invoice & feedback signal — in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateOrg(true)}
            className="px-3 py-2 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #6BA6F5, #1E4E8C)' }}
            data-testid="create-org-btn"
          >
            <Plus size={14} /> New Organization
          </button>
          <button
            onClick={() => navigate('/admin/customers/insights')}
            className="px-3 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center gap-2"
            style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
            data-testid="open-insights-btn"
          >
            <Lightbulb size={14} /> Q&amp;A Insights
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-2"
            style={{ background: '#F26B1F' }}
            data-testid="export-csv-btn"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <CreateOrgModal
        isOpen={showCreateOrg}
        onClose={() => setShowCreateOrg(false)}
        onCreated={() => {
          setShowCreateOrg(false);
          // Refresh customer list.
          adminCustomersApi.list({ type, q: q || undefined, sort, order, limit: 100, offset: 0 })
            .then(({ data }) => setData(data))
            .catch(() => {});
        }}
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="customer-kpis">
        <TILE label="Organizations" value={fmtNum(tc.organizations)} sub={`${fmtNum(tc.users_total)} users total`} icon={Building2} color="#6BA6F5" />
        <TILE label="Personal accounts" value={fmtNum(tc.personal)} sub="solo wallets" icon={User} color="#10B981" />
        <TILE
          label="Revenue"
          value={revInr ? fmtMoney(revInr.total, 'INR') : '₹0'}
          sub={revUsd ? `+ ${fmtMoney(revUsd.total, 'USD')} USD` : 'INR + USD'}
          icon={IndianRupee}
          color="#F26B1F"
        />
        <TILE
          label="Credits flow"
          value={fmtNum(Math.round(totalCreditsSold))}
          sub={`${fmtNum(Math.round(totalCreditsConsumed))} consumed`}
          icon={Wallet}
          color="#A78BFA"
        />
      </div>

      {/* Signups + countries */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-3">
        <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>SIGNUPS · LAST 90 DAYS</div>
            <div className="text-[10px] text-nexus-text-tertiary">organization vs personal</div>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={overview?.signups_series || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                <YAxis width={32} tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#0A1B2E', border: '1px solid #F26B1F', borderRadius: 8, fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="organization" stackId="a" fill="#6BA6F5" />
                <Bar dataKey="personal"     stackId="a" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="geography-card">
          <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>GEOGRAPHY</div>
          {topCountries.length === 0 ? (
            <p className="text-[12px] text-nexus-text-tertiary">No geo data yet — IPs are captured at signup.</p>
          ) : (
            <ul className="space-y-2">
              {topCountries.map((g) => (
                <li key={g.country_code} className="flex items-center justify-between text-[12px]">
                  <span className="inline-flex items-center gap-2 text-nexus-text-secondary">
                    <MapPin size={12} className="text-nexus-text-tertiary" /> {g.country_code}
                  </span>
                  <span className="font-mono text-nexus-text-primary">{g.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
          {[
            { v: 'all', label: 'All', icon: TrendingUp },
            { v: 'organization', label: 'Organizations', icon: Building2 },
            { v: 'personal', label: 'Personal', icon: User },
          ].map(t => (
            <button key={t.v} onClick={() => setType(t.v)}
              className="px-2.5 h-8 rounded-md text-[11px] inline-flex items-center gap-1.5 transition-all"
              style={{
                background: type === t.v ? 'var(--layer-0)' : 'transparent',
                color: type === t.v ? 'var(--nexus-text-primary)' : 'var(--nexus-text-tertiary)',
                fontWeight: type === t.v ? 600 : 400,
              }}
              data-testid={`tab-${t.v}`}>
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nexus-text-tertiary" />
          <input
            placeholder="Search by name, slug or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 h-9 rounded-lg text-[12px] text-nexus-text-primary outline-none"
            style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
            data-testid="customers-search"
          />
        </div>
        <span className="text-[11px] text-nexus-text-tertiary ml-auto">{total} matches</span>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead style={{ color: '#718096' }}>
              <tr>
                {[
                  { k: 'name', label: 'Customer' },
                  { k: 'users', label: 'Users' },
                  { k: 'consumed', label: 'Consumed' },
                  { k: 'revenue', label: 'Revenue' },
                  { k: 'created', label: 'Joined' },
                ].map(c => (
                  <th key={c.k} onClick={() => handleSort(c.k)}
                    className="text-left px-4 py-3 font-semibold cursor-pointer select-none uppercase tracking-wider text-[10px]"
                    data-testid={`sort-${c.k}`}>
                    {c.label}
                    {sort === c.k && <span className="ml-1">{order === 'desc' ? '↓' : '↑'}</span>}
                  </th>
                ))}
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Country</th>
                <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-[10px]">Activity</th>
                <th className="px-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 text-nexus-text-tertiary">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 text-nexus-text-tertiary">No customers match.</td></tr>
              )}
              {items.map((r) => {
                const isPersonal = r.account_type === 'personal';
                return (
                  <tr key={r.id}
                      className="border-t cursor-pointer hover:bg-white/[0.02] transition-colors"
                      style={{ borderColor: 'var(--hairline)' }}
                      onClick={() => navigate(`/admin/customers/${r.id}`)}
                      data-testid={`customer-row-${r.slug || r.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ background: isPersonal ? 'rgba(16,185,129,0.12)' : 'rgba(107,166,245,0.12)',
                                   color: isPersonal ? '#10B981' : '#6BA6F5' }}>
                          {isPersonal ? <User size={13} /> : <Building2 size={13} />}
                        </span>
                        <div className="min-w-0">
                          <div className="text-nexus-text-primary font-medium truncate max-w-[260px]">{r.name}</div>
                          <div className="text-nexus-text-tertiary text-[10.5px] truncate max-w-[260px]">{r.billing_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-nexus-text-secondary"><UsersIcon size={11} className="inline mr-1 text-nexus-text-tertiary" />{r.users}</td>
                    <td className="px-4 py-3 text-nexus-text-primary">
                      <div className="font-mono">{r.credits_consumed.toLocaleString(undefined, { maximumFractionDigits: 1 })} cr</div>
                      <div className="text-[10px] text-nexus-text-tertiary">{fmtNum(r.tokens)} tokens</div>
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {r.revenue_inr ? <div>{fmtMoney(r.revenue_inr, 'INR')}</div> : null}
                      {r.revenue_usd ? <div className="text-[10px] text-nexus-text-tertiary">{fmtMoney(r.revenue_usd, 'USD')}</div> : null}
                      {!r.revenue_inr && !r.revenue_usd ? <span className="text-nexus-text-tertiary">—</span> : null}
                    </td>
                    <td className="px-4 py-3 text-nexus-text-tertiary">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-nexus-text-secondary text-[11px]">
                      {r.country
                        ? <span className="inline-flex items-center gap-1"><MapPin size={10} className="text-nexus-text-tertiary" /> {r.country}{r.city ? ` · ${r.city}` : ''}</span>
                        : <span className="text-nexus-text-tertiary">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-nexus-text-secondary text-[11px] inline-flex items-center gap-3">
                        <span><Activity size={10} className="inline mr-0.5 text-nexus-text-tertiary" />{r.calls}</span>
                        <span><MessageSquare size={10} className="inline mr-0.5 text-nexus-text-tertiary" />{r.conversations}</span>
                      </div>
                    </td>
                    <td className="px-2 text-nexus-text-tertiary"><ChevronRight size={14} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomers;


// ============================================================
// Super-Admin: Create Organization modal
// Form: name, optional slug, multi-email admin invites, initial
// complimentary credits grant.
// ============================================================
const CreateOrgModal = ({ isOpen, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [admins, setAdmins] = useState('');
  const [initialCredits, setInitialCredits] = useState('0');
  const [sendInvites, setSendInvites] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) { setName(''); setSlug(''); setAdmins(''); setInitialCredits('0'); setSendInvites(true); }
  }, [isOpen]);

  if (!isOpen) return null;

  const adminEmails = admins
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));

  const submit = async () => {
    if (!name.trim()) { toast.error('Organization name is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await adminOrgsApi.create({
        name: name.trim(),
        slug: slug.trim() || undefined,
        admins: adminEmails,
        initial_credits: Number(initialCredits) || 0,
        send_invites: sendInvites,
      });
      const linkSummary = (data?.invites || []).filter(i => !i.email_sent).map(i => i.email);
      toast.success(
        `Organization "${data?.tenant?.name}" created` +
        (data?.tenant?.pool_wallet?.credit_balance > 0 ? ` · ${data.tenant.pool_wallet.credit_balance} credits` : '') +
        (linkSummary.length ? ` · activation links generated for ${linkSummary.length} admin(s)` : '')
      );
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-org-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-[520px] max-w-[92vw] rounded-2xl p-6 space-y-4"
        style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>Control Plane</div>
            <h2 className="font-heading text-xl font-semibold mt-1">New Organization</h2>
            <p className="text-[12px] text-nexus-text-tertiary mt-1">
              Create a tenant, seed it with credits, and invite one or more admins.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5"><X size={16} /></button>
        </div>

        <div className="space-y-3">
          <Field label="Organization name">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Innovations Pvt Ltd"
              className="w-full px-3 py-2 rounded-lg text-[13px]"
              style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
              data-testid="create-org-name"
            />
          </Field>
          <Field label="Slug (URL-safe, optional)" hint="Auto-generated from name if left blank">
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="acme-innovations"
              className="w-full px-3 py-2 rounded-lg text-[13px]"
              style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
              data-testid="create-org-slug"
            />
          </Field>
          <Field
            label="Admin emails"
            hint={`Comma / space / newline separated · ${adminEmails.length} valid email${adminEmails.length === 1 ? '' : 's'}`}
          >
            <textarea
              value={admins}
              onChange={(e) => setAdmins(e.target.value)}
              placeholder="founder@acme.com, ceo@acme.com"
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-[13px] resize-none"
              style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
              data-testid="create-org-admins"
            />
          </Field>
          <Field label="Complimentary credit grant" hint="Lands in the org pool; org admin then allocates per user.">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="500"
                value={initialCredits}
                onChange={(e) => setInitialCredits(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-[13px]"
                style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
                data-testid="create-org-credits"
              />
              <span className="text-[11px] text-nexus-text-tertiary">credits</span>
            </div>
          </Field>
          <label className="flex items-center gap-2 text-[12px] text-nexus-text-secondary mt-1 cursor-pointer">
            <input
              type="checkbox"
              checked={sendInvites}
              onChange={(e) => setSendInvites(e.target.checked)}
              data-testid="create-org-send-invites"
            />
            Email activation links to admins now
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'var(--hairline)' }}>
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-[12px] font-semibold"
            style={{ background: 'var(--layer-2)', color: 'var(--nexus-text-secondary)' }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !name.trim()}
            className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #6BA6F5, #1E4E8C)' }}
            data-testid="create-org-submit"
          >
            {submitting ? 'Creating…' : 'Create organization'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <div className="text-[10px] font-semibold tracking-wider uppercase text-nexus-text-tertiary mb-1.5">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-nexus-text-tertiary mt-1">{hint}</div>}
  </div>
);
